import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "unreplied", label: "Needs reply" },
  { id: "pinned", label: "Pinned" },
];

export default function OwnerMessages() {
  const [data, setData] = useState({ messages: [], stats: { total: 0, unreplied: 0, pinned: 0 } });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [replyId, setReplyId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/messages");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load messages");
    }
  };

  useEffect(() => { (async () => { try { await refresh(); } finally { setLoading(false); } })(); }, []);

  const patch = async (m, body, optimisticLabel) => {
    setData((prev) => ({
      ...prev,
      messages: prev.messages.map((x) => (x.message_id === m.message_id ? { ...x, ...body } : x)),
    }));
    try {
      await api.patch(`/admin/messages/${m.message_id}`, body);
      if (optimisticLabel) toast.success(optimisticLabel);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      await refresh();
    }
  };

  const remove = async (m) => {
    if (!confirm(`Delete message from ${m.name}?`)) return;
    try {
      await api.delete(`/admin/messages/${m.message_id}`);
      setData((prev) => ({ ...prev, messages: prev.messages.filter((x) => x.message_id !== m.message_id) }));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const openReply = (m) => {
    setReplyId(m.message_id);
    setOpenId(m.message_id);
    setReplyText(replyId === m.message_id ? replyText : `Hi ${(m.name || "there").split(" ")[0]},\n\n`);
  };

  const cancelReply = () => {
    setReplyId(null);
    setReplyText("");
  };

  const sendReply = async (m) => {
    const body = replyText.trim();
    if (body.length < 2) {
      toast.error("Reply is too short");
      return;
    }
    setSending(true);
    try {
      const { data: updated } = await api.post(`/admin/messages/${m.message_id}/reply`, { body });
      setData((prev) => ({
        ...prev,
        messages: prev.messages.map((x) => (x.message_id === m.message_id ? updated : x)),
        stats: {
          ...prev.stats,
          unreplied: Math.max(0, prev.stats.unreplied - (m.replied ? 0 : 1)),
        },
      }));
      toast.success(`Reply sent to ${m.reply_email}`);
      cancelReply();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply failed to send");
    } finally {
      setSending(false);
    }
  };

  const visible = (data.messages || []).filter((m) => {
    if (filter === "unreplied") return !m.replied;
    if (filter === "pinned") return !!m.pinned;
    return true;
  });

  if (loading) return <div className="text-[#5C6B61]">Loading messages…</div>;

  return (
    <section className="space-y-6" data-testid="owner-messages">
      <header>
        <div className="ff-label">Support inbox</div>
        <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          Customer messages
        </h1>
        <p className="text-sm text-[#5C6B61] mt-2">
          Every &ldquo;Get in touch&rdquo; submission lands here and pings your inbox. Hit <strong>Reply</strong> to respond from <strong>hello@smartershop.xyz</strong> — your reply is logged on the message.
        </p>
      </header>

      <div className="grid sm:grid-cols-3 gap-3">
        <Stat label="Total" value={data.stats.total} />
        <Stat label="Needs reply" value={data.stats.unreplied} accent={data.stats.unreplied > 0 ? "#C36952" : null} />
        <Stat label="Pinned" value={data.stats.pinned} />
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="messages-filter">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`filter-${f.id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
              filter === f.id ? "bg-[#2A332C] text-white" : "bg-white text-[#5C6B61] border border-[#E5E2DA] hover:bg-[#F3F0EA]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="ff-card p-8 text-center text-sm text-[#5C6B61]" data-testid="messages-empty">
          No messages here.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="messages-list">
          {visible.map((m) => {
            const isOpen = openId === m.message_id;
            return (
              <li key={m.message_id} className="ff-card overflow-hidden" data-testid={`message-${m.message_id}`}>
                <div className="p-4 md:p-5 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#3B5A3E] text-white flex items-center justify-center text-sm font-semibold flex-none">
                    {(m.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-[#2A332C] truncate">{m.subject}</div>
                      {m.pinned && <span className="text-[10px] uppercase tracking-wider font-medium bg-[#FFF4E5] text-[#8A5C12] px-2 py-0.5 rounded-full">Pinned</span>}
                      {m.replied ? (
                        <span className="text-[10px] uppercase tracking-wider font-medium bg-[#E8F0E9] text-[#3B5A3E] px-2 py-0.5 rounded-full">Replied</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider font-medium bg-[#FBE5DE] text-[#A33D26] px-2 py-0.5 rounded-full">Needs reply</span>
                      )}
                    </div>
                    <div className="text-xs text-[#5C6B61] mt-1 truncate">
                      From <strong className="text-[#2A332C]">{m.name}</strong> · {new Date(m.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <button
                      onClick={() => setOpenId(isOpen ? null : m.message_id)}
                      data-testid={`message-toggle-${m.message_id}`}
                      className="text-xs text-[#3B5A3E] hover:underline mt-2"
                    >
                      {isOpen ? "Hide message ↑" : "View message ↓"}
                    </button>
                    {isOpen && (
                      <div className="mt-3 p-3 rounded-xl bg-[#F3F0EA] text-sm text-[#2A332C] whitespace-pre-wrap" data-testid={`message-body-${m.message_id}`}>
                        {m.message}
                      </div>
                    )}
                    {isOpen && Array.isArray(m.replies) && m.replies.length > 0 && (
                      <div className="mt-3 space-y-2" data-testid={`message-history-${m.message_id}`}>
                        <div className="ff-label">Reply history</div>
                        {m.replies.map((r, i) => (
                          <div key={i} className="p-3 rounded-xl bg-[#E8F0E9] border border-[#D4E0D5] text-sm text-[#2A332C] whitespace-pre-wrap">
                            <div className="text-[11px] text-[#5C6B61] mb-1">
                              Sent {new Date(r.sent_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                            </div>
                            {r.body}
                          </div>
                        ))}
                      </div>
                    )}
                    {replyId === m.message_id && (
                      <div className="mt-3 space-y-2" data-testid={`message-compose-${m.message_id}`}>
                        <div className="text-xs text-[#5C6B61]">
                          Replying to <strong className="text-[#2A332C]">{m.reply_email}</strong> from <strong className="text-[#2A332C]">hello@smartershop.xyz</strong>
                        </div>
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          rows={6}
                          placeholder="Type your reply…"
                          data-testid={`reply-textarea-${m.message_id}`}
                          className="w-full px-3 py-2 rounded-xl border border-[#E5E2DA] bg-white text-sm text-[#2A332C] focus:outline-none focus:ring-2 focus:ring-[#3B5A3E]/30 resize-y"
                        />
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => sendReply(m)}
                            disabled={sending || replyText.trim().length < 2}
                            data-testid={`reply-send-${m.message_id}`}
                            className="px-4 py-2 rounded-full text-xs font-semibold text-white bg-[#3B5A3E] hover:bg-[#2A332C] disabled:opacity-50 disabled:cursor-not-allowed transition"
                          >
                            {sending ? "Sending…" : "Send reply"}
                          </button>
                          <button
                            onClick={cancelReply}
                            disabled={sending}
                            data-testid={`reply-cancel-${m.message_id}`}
                            className="px-3 py-2 rounded-full text-xs text-[#5C6B61] hover:bg-[#F3F0EA] transition"
                          >
                            Cancel
                          </button>
                          <span className="ml-auto text-[11px] text-[#8B9690]">{replyText.length} / 5000</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="px-4 md:px-5 py-3 bg-[#FAF9F6] border-t border-[#F0EDE7] flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => openReply(m)}
                    data-testid={`message-reply-${m.message_id}`}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-[#3B5A3E] hover:bg-[#2A332C] transition"
                  >
                    ✉ Reply
                  </button>
                  <a
                    href={`mailto:${m.reply_email}?subject=Re: ${encodeURIComponent(m.subject)}`}
                    data-testid={`message-mailto-${m.message_id}`}
                    className="text-xs text-[#5C6B61] hover:text-[#3B5A3E] hover:underline"
                  >
                    or open in mail app
                  </a>
                  <span className="text-[#E5E2DA]">·</span>
                  <button
                    onClick={() => patch(m, { replied: !m.replied }, m.replied ? "Marked unread" : "Marked replied")}
                    data-testid={`message-mark-replied-${m.message_id}`}
                    className="text-xs text-[#5C6B61] hover:text-[#3B5A3E]"
                  >
                    {m.replied ? "Mark unread" : "Mark replied"}
                  </button>
                  <span className="text-[#E5E2DA]">·</span>
                  <button
                    onClick={() => patch(m, { pinned: !m.pinned }, m.pinned ? "Unpinned" : "Pinned")}
                    data-testid={`message-pin-${m.message_id}`}
                    className="text-xs text-[#5C6B61] hover:text-[#8A5C12]"
                  >
                    {m.pinned ? "Unpin" : "Pin"}
                  </button>
                  <button
                    onClick={() => remove(m)}
                    data-testid={`message-delete-${m.message_id}`}
                    className="ml-auto text-xs text-[#A65541] hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="ff-card p-4">
      <div className="ff-label">{label}</div>
      <div className="text-2xl font-semibold mt-1" style={{ color: accent || "#2A332C", fontFamily: "Outfit, sans-serif" }}>
        {value}
      </div>
    </div>
  );
}
